export const ARC_STAGES = [
	"confident",
	"resisting",
	"conceding",
	"seeking_fix",
] as const;
export type ArcStage = (typeof ARC_STAGES)[number];

export const PROETHICS_SCENARIO_IDS = [
	"scenario-1",
	"scenario-2",
	"scenario-3",
] as const;
export type ProethicsScenarioId = (typeof PROETHICS_SCENARIO_IDS)[number];

export type AvatarRole = "daria" | "dennis" | "smith";

export type TranscriptRole = "mentor" | AvatarRole;

export type AudioStatus = "pending" | "ready" | "unavailable";

export type TranscriptMessage = {
	id: string;
	role: TranscriptRole;
	text: string;
	audioBase64?: string;
	audioUrl?: string;
	/** pending while TTS runs, ready once a playable source exists, unavailable if TTS failed/timed out/aborted. */
	audioStatus?: AudioStatus;
};

// The app transcribes audio client-side, so it only ever sends text turns.
export type LearnerInput = { modality: "text"; text: string };

export type TurnRequest = {
	scenarioId: string;
	arcStage: ArcStage;
	transcript: { role: TranscriptRole; text: string }[];
	learnerInput: LearnerInput;
	// Last hint shown to the learner; the server's hint agent keys its
	// repeat-when-stuck logic off it. "" is never sent (omitted instead).
	previousHint?: string;
	// Absent on the first turn; the server creates the memory thread and
	// returns it via `data-thread-created`.
	threadId?: string;
};

export type SummativeCriterion = {
	name: string;
	description: string;
	reason: string;
	score: number;
};

export type SummativeResult = {
	criteria: SummativeCriterion[];
	feedback: string;
};

export type StreamPart =
	| { type: "data-learner-transcript"; data: { text: string } }
	| { type: "data-thread-created"; data: { threadId: string } }
	| { type: "data-avatar-reply"; data: { reply: string } }
	| { type: "data-avatar-audio-chunk"; data: { chunkBase64: string } }
	| { type: "data-avatar-audio-done"; data: Record<string, never> }
	| { type: "data-avatar-audio-url"; data: { url: string } }
	| { type: "data-turn-state"; data: { arcStage: ArcStage } }
	| { type: "data-hint"; data: { hint: string } }
	| { type: "data-summative-stream"; data: Partial<SummativeResult> }
	| { type: "data-final-result"; data: SummativeResult };

/** Client-side turn transitions; `audio-assembled` carries an empty string when TTS produced nothing. */
export type TurnEvent =
	| StreamPart
	| { type: "audio-assembled"; data: { audioBase64: string } }
	| { type: "audio-timeout" }
	| { type: "audio-aborted" }
	| { type: "stream-closed" };
